import React, { useEffect, useMemo, useState } from 'react';
import masterDataService from '../../service/masterData.service';
import useMenus from '../../context/MenuContext';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { generateAutocompleteItems, getIcon } from '../../utils/helper';
import { AutoCompleteSelectUI, ButtonUI, DatePickerUI, FormLabelUI } from '../Interface';
import DoneIcon from '@mui/icons-material/Done';
import serviceOperationService from '../../service/serviceOperation.service';
import OperationDialog from './OperationDialog';
import serviceCoreService from '../../service/serviceCore.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import ProjectAddDialog from '../Project/ProjectAddDialog/ProjectAddDialog';
import dayjs from 'dayjs';
import WorkAreaDialog from './WorkAreaDialog';
import DescriptionDialog from './DescriptionDialog';
import propertyService from '../../service/property.service';
import useParam from '../../context/ParamContext';
import { ItemKind } from '../../config/item';
import ItemAddDialog from '../Item/ItemAddDialog/ItemAddDialog';

const LOCAL_STORAGE_KEY = 'serviceWorksheetCreatorTwo';

export const WorkSheetCreatorPageTwo = () => {
  const def = {
    projectId: null,
    itemCode: null,
    serviceTask: [],
    serviceTaskTypeId: null,
    serviceMaterial: [],
    serviceTaskCheckList: [],
    dueDate: dayjs(),
    companyId: null,
    priorityId: null,
  };
  const { getMenus, menus, getSubMenuNamesByUrls, getMenuSubMenuId } = useMenus();
  const { params, getParam } = useParam();
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [operationsData, setOperationsData] = useState([]);
  const [operationDialogOpen, setOperationDialogOpen] = useState(false);
  const [projectAddDialogOpen, setProjectAddDialogOpen] = useState(false);
  const [itemAddDialogOpen, setItemAddDialogOpen] = useState(false);
  const [projectSubmenuId, setProjectSubmenuId] = useState(null);
  const [serviceSubmenuId, setServiceSubmenuId] = useState(null);
  const [dynProps, setDynProps] = useState([]);

  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    projects: [],
    items: [],
    company: [],
    priority: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({});
  const [options, setOptions] = useState({
    types: [],
    groups: [],
    workAreas: [],
  });
  const [values, setValues] = useState(def);
  const [refetch, setRefetch] = useState(false);
  const [workAreaDialogOpen, setWorkAreaDialogOpen] = useState(false);
  const [selectedOperations, setSelectedOperations] = useState([]);
  const [initialized, setInitialized] = useState(false);

  const defDescDialogData = {
    open: false,
    note: '',
    taskId: null,
  };
  const [descDialogData, setDescDialogData] = useState(defDescDialogData);

  const isSmallScreen = useMediaQuery('(max-width:960px)');

  useEffect(() => {
    getMenus();
    getParam();
  }, []);

  useEffect(() => {
    initDefaultValues();
  }, [serviceSubmenuId]);

  useEffect(() => {
    if (!Object.keys(menus).length) return;
    setProjectSubmenuId(getMenuSubMenuId('Project'));
    setServiceSubmenuId(getMenuSubMenuId('service'));
  }, [menus]);

  useEffect(() => {
    if (refetch) initDefaultValues();
  }, [refetch]);

  useEffect(() => {
    if (projectSubmenuId && params.WORKSHEET_SHOW_DYN_PROPS_BY_PROPERTYID) getFormProperties();
  }, [projectSubmenuId, params]);

  useEffect(() => {
    if (!initialized) return;

    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!saved) return;

    const parsed = JSON.parse(saved);

    setValues((prev) => ({
      ...prev,
      ...parsed,
      dueDate: dayjs(parsed.dueDate),
    }));
  }, [initialized]);

  useEffect(() => {
    if (!initialized) return;

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(values));
  }, [values, initialized]);

  const getFormProperties = () => {
    propertyService.getPropertiesByFormId(projectSubmenuId).then((data) => {
      setDynProps(data);
    });
  };

  const initDefaultValues = () => {
    if (!serviceSubmenuId) return;

    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .serviceTaskTypeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .serviceGroupForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .workAreaToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .itemsFromTableToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .priorityToForm(serviceSubmenuId)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          projects: resp[0],
          types: resp[1],
          groups: resp[2],
          workAreas: resp[3],
          items: resp[4],
          company: resp[5],
          priority: resp[6],
        };

        setAutoCompleteData(aData);

        const filteredItems = aData.items.filter((item) => item.kind === ItemKind.EQUIPMENT && item.active);

        const acOptions = {
          projects: generateAutocompleteItems(aData.projects, ['projectName', 'orderNumber'], 'projectId'),
          items: generateAutocompleteItems(filteredItems, ['itemCode', 'itemName'], 'itemCode'),
          company: generateAutocompleteItems(aData.company, 'name', 'id'),
          priority: generateAutocompleteItems(aData.priority.priorityListItems, 'itemName', 'itemId'),
        };

        setOptions({
          types: aData.types,
          groups: aData.groups,
          workAreas: aData.workAreas,
        });
        setAutoCompleteOptions(acOptions);

        setValues((prev) => ({
          ...prev,
          priorityId: aData.priority?.defaultValue ?? null,
        }));

        setRefetch(false);
        setInitialized(true);
      })
      .finally(() => {});
  };

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['workSheetCreator']);
  }, [menus]);

  const getServiceOperationsByGroupId = (groupId) => {
    setOperationDialogOpen(true);
    setSelectedGroupId(groupId);
    serviceOperationService.getItemsByGroupId(groupId).then((data) => {
      setOperationsData(data);
    });
  };

  const createData = () => {
    if (!values.projectId && params.HIDE_SERVICE_PROJECT_FIELD !== 'Y') {
      addNotification({
        content: `Projekt választása kötelező!`,
        type: NotificationType.ERROR,
      });
      return;
    }

    if (!values.itemCode && params.SHOW_SERVICE_ITEMCODE_FIELD === 'Y') {
      addNotification({
        content: `Cikk választása kötelező!`,
        type: NotificationType.ERROR,
      });
      return;
    }

    if (!values.serviceTaskTypeId) {
      addNotification({
        content: `Típus választása kötelező!`,
        type: NotificationType.ERROR,
      });
      return;
    }

    if (!values.priorityId) {
      addNotification({
        content: `Prioritás választása kötelező!`,
        type: NotificationType.ERROR,
      });
      return;
    }

    serviceCoreService.createWorksheet(values).then((data) => {
      if (Object.keys(data).length > 0) {
        addNotification({
          content: `${data.workingNumber} számú munkalap elkészült!`,
          type: NotificationType.SUCCESS,
        });
        handleDropAll();
      }
    });
  };

  const handleDropAll = () => {
    setSelectedGroupId(null);

    setValues({
      ...def,
      priorityId: autoCompleteData.priority?.defaultValue ?? null,
    });
  };

  const selectedWorkArea = options.workAreas.find(
    (workArea) => workArea.id === values.serviceTask[0]?.workAreaId
  )?.name;

  return (
    <Box
      backgroundColor="#E9F5FE"
      minHeight={isSmallScreen ? '100vh' : 'calc(100vh - 65px)'}
      display="flex"
      justifyContent="center"
      alignItems="start"
      pt={isSmallScreen ? '0.5rem' : '2rem'}
    >
      <Box
        backgroundColor="#fff"
        width={isSmallScreen ? '95%' : '900px'}
        minHeight={isSmallScreen ? 'calc(100vh - 1rem)' : '100%)'}
        borderRadius="0.5rem"
        px="1rem"
        overflow="hidden"
      >
        <Typography fontWeight="bold" textAlign="center" py="0.5rem" fontSize="large">
          {submenuNames[0]}
        </Typography>

        <Divider />

        <Box
          sx={{
            display: 'flex',
            gap: '0.3rem',
            overflowX: 'auto',
            overflowY: 'hidden',
            m: '0.8rem 0rem',
          }}
        >
          {options?.types?.map((type) => {
            const isActive = values.serviceTaskTypeId === type.id;

            return (
              <Box key={type.id} sx={{ flex: '0 0 auto' }}>
                <ButtonUI
                  text={type.name}
                  size="sm"
                  onClick={() =>
                    setValues((prev) => ({
                      ...prev,
                      serviceTaskTypeId: type.id,
                    }))
                  }
                  style={{
                    backgroundColor: type.color,
                    border: isActive ? '2px solid grey' : '2px solid transparent',
                  }}
                />
              </Box>
            );
          })}
        </Box>

        <Grid container spacing={1.5} my="0.8rem">
          {params.HIDE_SERVICE_PROJECT_FIELD !== 'Y' && (
            <Grid item xs={12} sm={8} md={6}>
              <Box display="flex">
                <IconButton onClick={() => setProjectAddDialogOpen(true)}>{getIcon('Add', '2rem')}</IconButton>

                <AutoCompleteSelectUI
                  fullWidth
                  label={<FormLabelUI text="Projekt" />}
                  dataset={autoCompleteOptions.projects}
                  onChange={(_e, newVal) => {
                    setValues((prevState) => ({
                      ...prevState,
                      projectId: newVal?.value ?? null,
                    }));
                  }}
                  selectedValue={values.projectId}
                  variant="outlined"
                />
              </Box>
            </Grid>
          )}

          {params.SHOW_SERVICE_ITEMCODE_FIELD === 'Y' && (
            <Grid item xs={12} sm={8} md={6}>
              <Box display="flex">
                <IconButton onClick={() => setItemAddDialogOpen(true)}>{getIcon('Add', '2rem')}</IconButton>

                <AutoCompleteSelectUI
                  fullWidth
                  label={<FormLabelUI text="Cikk" />}
                  dataset={autoCompleteOptions.items}
                  onChange={(_e, newVal) => {
                    setValues((prevState) => ({
                      ...prevState,
                      itemCode: newVal?.value ?? null,
                    }));
                  }}
                  selectedValue={values.itemCode}
                  variant="outlined"
                />
              </Box>
            </Grid>
          )}

          <Grid item xs={12} sm={4} md={3}>
            <DatePickerUI
              label={<FormLabelUI text="Határidő" />}
              value={values.dueDate}
              clearable={false}
              variant="outlined"
              onChange={(newDate) => {
                setValues((prevState) => ({
                  ...prevState,
                  dueDate: newDate,
                }));
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AutoCompleteSelectUI
              fullWidth
              label={<FormLabelUI text="Cég" />}
              dataset={autoCompleteOptions.company}
              onChange={(_e, newVal) => {
                setValues((prevState) => ({
                  ...prevState,
                  companyId: newVal?.value ?? null,
                }));
              }}
              selectedValue={values.companyId}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <AutoCompleteSelectUI
              fullWidth
              label={<FormLabelUI text="Prioritás" />}
              dataset={autoCompleteOptions.priority}
              onChange={(_e, newVal) => {
                setValues((prevState) => ({
                  ...prevState,
                  priorityId: newVal?.value ?? null,
                }));
              }}
              selectedValue={values.priorityId}
              variant="outlined"
              disableClearable
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            display: 'flex',
            gap: '0.3rem',
            overflowX: 'auto',
            overflowY: 'hidden',
            m: '0.8rem 0rem',
          }}
        >
          {options?.groups?.map((group) => {
            const isActive = selectedGroupId === group.id;

            return (
              <Box key={group.id} sx={{ flex: '0 0 auto' }}>
                <ButtonUI
                  text={group.name}
                  size="sm"
                  onClick={() => getServiceOperationsByGroupId(group.id)}
                  style={{
                    backgroundColor: group.color,
                    border: isActive ? '2px solid grey' : '2px solid transparent',
                  }}
                />
              </Box>
            );
          })}
        </Box>

        <Box m="0.8rem 0rem" textAlign="center">
          <ButtonUI
            text={`Munkaállomások ${selectedWorkArea ? `(${selectedWorkArea})` : ''}`}
            size="sm"
            variant="gradient"
            color="blue-gray"
            onClick={() => setWorkAreaDialogOpen(true)}
            disabled={values.serviceTask.length === 0}
          />
        </Box>

        <Divider />

        <Box py="0.8rem">
          <Typography>Kiválasztott tevékenységek:</Typography>
          <List sx={{ m: 0, p: 0 }}>
            {values.serviceTask.map((task) => (
              <ListItem key={task.id} sx={{ m: 0, p: 0 }}>
                <ListItemIcon sx={{ minWidth: '30px' }}>
                  <DoneIcon />
                </ListItemIcon>
                <ListItemText primary={task.name} secondary={task.note ?? ''} />
              </ListItem>
            ))}
          </List>
        </Box>

        <Box pb="0.5rem" display="flex" gap={0.5} justifyContent="center" flexWrap="wrap">
          <ButtonUI
            text="Munkalap létrehozása"
            className="bg-primary"
            disabled={values.serviceTask.length === 0}
            onClick={() => createData()}
          />
          <ButtonUI text="Mégsem" className="bg-danger" onClick={() => handleDropAll()} />
        </Box>
      </Box>

      <OperationDialog
        open={operationDialogOpen}
        handleClose={() => setOperationDialogOpen(false)}
        data={operationsData}
        valuesState={{ values, setValues }}
        selectedGroupId={selectedGroupId}
        setDescDialogData={setDescDialogData}
        operationsState={{ selectedOperations, setSelectedOperations }}
      />

      <WorkAreaDialog
        open={workAreaDialogOpen}
        handleClose={() => setWorkAreaDialogOpen(false)}
        workAreas={options.workAreas}
        setValues={setValues}
      />

      <DescriptionDialog
        descDialogData={descDialogData}
        handleClose={() => setDescDialogData(defDescDialogData)}
        onChangeNote={(note) => {
          setSelectedOperations((prev) => prev.map((op) => (op.id === descDialogData.taskId ? { ...op, note } : op)));
        }}
      />

      <ProjectAddDialog
        open={projectAddDialogOpen}
        handleClose={() => setProjectAddDialogOpen(false)}
        setRefetch={setRefetch}
        setValues={setValues}
        dynProps={dynProps}
      />

      {itemAddDialogOpen && (
        <ItemAddDialog
          open={itemAddDialogOpen}
          handleClose={() => setItemAddDialogOpen(false)}
          setRefetch={setRefetch}
          setValues={setValues}
        />
      )}
    </Box>
  );
};
